import { FC, useId, useState } from 'react'
import { Box, Button, CircularProgress, Divider, Menu, MenuItem, Tooltip } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useSnackbar } from 'notistack'
import { oportunidadeService } from '@/services/oportunidadeService'
import { Oportunidade } from '@/types/oportunidade'
import { STATUS_LABEL, StatusReserva } from '@/types/statusReserva'
import { DadosPerda, ErroMotivoPerda, MSG_CONFLITO_VERSAO } from '@/types/scoreComercial'
import { MotivoPerdaDialog, DestinoPerda } from './MotivoPerdaDialog'
import { ehConflitoVersao, erroMotivoPerdaDe, mensagemErroDe } from './erros'
import { DESTINOS_PERDA, MSG_MOVER, destinosPermitidos, ehDestinoPerda } from './transicaoReserva'

interface SeletorEtapaReservaProps {
  reservaId: string
  empresa: string
  status: StatusReserva
  /** Versão lida — vai em `versaoEsperada` (409 `CONFLITO_VERSAO` se a reserva mudou). */
  versao?: number | null
  /** Ignorado pelo backend desde 28/09 (o autor vem do token); o contrato ainda pede. */
  usuarioId: string
  disabled?: boolean
  /** Transição aceita pelo servidor; `atualizada` é a resposta do PATCH (pode faltar). */
  onMovida: (atualizada: Oportunidade | null, de: StatusReserva, para: StatusReserva) => void | Promise<void>
  /** 409: a reserva mudou desde a leitura — quem chama recarrega. */
  onConflito: () => void | Promise<void>
}

/**
 * "Mover para…" de uma reserva (entrega F, 16/10/2026): oferece SÓ as etapas que a máquina
 * de status permite a partir da atual (`TRANSICOES`, na ordem da jornada) — a mesma regra
 * do Kanban, sem arrastar. Perdas (Cancelada / Não convertida) abrem o diálogo de motivo do
 * catálogo antes de qualquer chamada. O backend continua sendo a autoridade: revalida a
 * transição e devolve o erro, que aparece como veio.
 *
 * Sem hooks do router de propósito: a tela que usa (Estruturação) é testada com o
 * react-router-dom mockado só com `useNavigate`.
 */
export const SeletorEtapaReserva: FC<SeletorEtapaReservaProps> = ({
  reservaId,
  empresa,
  status,
  versao,
  usuarioId,
  disabled = false,
  onMovida,
  onConflito,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const menuId = useId()
  const [ancora, setAncora] = useState<HTMLElement | null>(null)
  const [movendo, setMovendo] = useState(false)
  const [perda, setPerda] = useState<DestinoPerda | null>(null)
  const [erroPerda, setErroPerda] = useState<ErroMotivoPerda | null>(null)

  const destinos = destinosPermitidos(status)
  const normais = destinos.filter((s) => !ehDestinoPerda(s))
  const perdas = destinos.filter((s) => ehDestinoPerda(s))
  const semDestino = destinos.length === 0
  const aberto = !!ancora

  /**
   * Chama o PATCH de status. Devolve true quando o servidor aceitou; erros de motivo de
   * perda ficam no diálogo (aponta o campo), conflito avisa e pede recarga.
   */
  const transicionar = async (para: StatusReserva, dadosPerda?: DadosPerda): Promise<boolean> => {
    setMovendo(true)
    let atualizada: Oportunidade | null = null
    try {
      atualizada =
        (await oportunidadeService.transicionarStatus(reservaId, {
          novoStatus: para,
          usuarioId,
          versaoEsperada: versao ?? null,
          ...(dadosPerda
            ? { observacao: dadosPerda.observacao, motivoPerdaCodigo: dadosPerda.motivoPerdaCodigo }
            : {}),
        })) ?? null
    } catch (e) {
      const erroCampo = dadosPerda ? erroMotivoPerdaDe(e) : null
      if (erroCampo) {
        setErroPerda(erroCampo)
      } else if (ehConflitoVersao(e)) {
        setPerda(null)
        enqueueSnackbar(MSG_CONFLITO_VERSAO, { variant: 'warning' })
        await onConflito()
      } else {
        enqueueSnackbar(mensagemErroDe(e, MSG_MOVER), { variant: 'error' })
      }
      return false
    } finally {
      setMovendo(false)
    }
    setPerda(null)
    enqueueSnackbar(`${empresa}: ${STATUS_LABEL[status]} → ${STATUS_LABEL[para]}`, { variant: 'success' })
    await onMovida(atualizada, status, para)
    return true
  }

  const escolher = (para: StatusReserva) => {
    setAncora(null)
    const destinoPerda = DESTINOS_PERDA[para]
    if (destinoPerda) {
      // Perda exige motivo do catálogo: nada vai ao servidor antes do diálogo.
      setErroPerda(null)
      setPerda(destinoPerda)
      return
    }
    void transicionar(para)
  }

  const botao = (
    <Button
      variant="contained"
      size="small"
      endIcon={movendo ? <CircularProgress size={14} color="inherit" /> : <ArrowDropDownIcon />}
      aria-label={`Mover para… — ${empresa}`}
      aria-haspopup="menu"
      aria-expanded={aberto ? 'true' : undefined}
      aria-controls={aberto ? menuId : undefined}
      onClick={(e) => setAncora(e.currentTarget)}
      disabled={disabled || semDestino || movendo}
    >
      Mover para…
    </Button>
  )

  return (
    <>
      {semDestino ? (
        // Tooltip em botão desabilitado precisa de um elemento que receba o hover.
        <Tooltip title="Etapa final — sem movimentação">
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {botao}
          </Box>
        </Tooltip>
      ) : (
        botao
      )}
      <Menu
        anchorEl={ancora}
        open={aberto}
        onClose={() => setAncora(null)}
        // O id vai na lista (role="menu"), que é o que o aria-controls do botão aponta.
        MenuListProps={{ id: menuId, 'aria-label': `Etapas para ${empresa}` }}
      >
        {normais.map((s) => (
          <MenuItem key={s} onClick={() => escolher(s)}>
            {STATUS_LABEL[s]}
          </MenuItem>
        ))}
        {perdas.length > 0 && normais.length > 0 && <Divider />}
        {perdas.map((s) => (
          <MenuItem key={s} onClick={() => escolher(s)} sx={{ color: 'error.main' }}>
            {`${STATUS_LABEL[s]} (exige motivo)`}
          </MenuItem>
        ))}
      </Menu>
      <MotivoPerdaDialog
        open={!!perda}
        empresa={empresa}
        destino={perda ?? undefined}
        submitting={movendo}
        erro={erroPerda}
        onClose={() => {
          if (movendo) return
          setPerda(null)
          setErroPerda(null)
        }}
        onConfirm={(dados) => {
          if (perda) void transicionar(perda, dados)
        }}
      />
    </>
  )
}
